import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OperonLogo } from "@/components/OperonLogo";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { Sun, Moon, ArrowRight } from "lucide-react";

export default function Login() {
  const { theme, toggleTheme } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/app/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-dvh grid lg:grid-cols-2">
      <div className="hidden lg:flex items-center justify-center relative overflow-hidden bg-card">
        <div className="absolute inset-0 operon-navy-band" />
        <div className="relative p-12 max-w-md">
          <OperonLogo theme="dark" size={44} />
          <h2 className="mt-8 font-heading text-3xl font-semibold tracking-tight">Smart Control for Stock &amp; Operations</h2>
          <p className="mt-3 text-muted-foreground">Real-time inventory, production orders with BOM, barcode scanning, PDF reports — all in one operational platform.</p>
          <ul className="mt-6 text-sm text-muted-foreground space-y-2">
            <li>• 14-day free trial, full features</li>
            <li>• Mobile-first for warehouse usage</li>
            <li>• Multi-user, multi-location</li>
          </ul>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <Link to="/" className="flex items-center">
            <OperonLogo theme={theme} size={32} />
          </Link>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6">
          <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="login-form">
            <div>
              <h1 className="font-heading text-2xl font-semibold tracking-tight">Welcome back</h1>
              <p className="text-sm text-muted-foreground mt-1">Log in to access your workspace</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" autoComplete="current-password" />
            </div>
            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in…" : <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot-password" className="text-muted-foreground hover:text-primary" data-testid="login-forgot-link">Forgot password?</Link>
              <Link to="/register" className="text-primary hover:underline" data-testid="login-to-register-link">Create account</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
